import { useEffect, useMemo, useRef, useState } from "react";
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle } from "../../../bootstrap/Modal";
import Card, { CardBody, CardHeader, CardTitle } from "../../../bootstrap/Card";
import Button from "../../../bootstrap/Button";
import Alert from "../../../bootstrap/Alert";
import Badge from "../../../bootstrap/Badge";
import Checks from "../../../bootstrap/forms/Checks";
import PreAlert from "../../../../helpers/utils/preAlert";
import api from "../../../../services/api";

const STEP_TITLES = [
    "Categorias",
    "Produtos",
    "Cat. Complemento",
    "Complementos",
    "Preview",
];

const ENTITY_KEYS = ["category", "product", "complementCategory", "complement", "item"];
const ENTITY_LABELS = {
    category: "Categorias",
    product: "Produtos",
    complementCategory: "Cat. Complemento",
    complement: "Complementos",
    item: "Itens",
};

const createEmptySummary = () => ({
    category: { selected: 0, success: 0, error: 0, skipped: 0 },
    product: { selected: 0, success: 0, error: 0, skipped: 0 },
    complementCategory: { selected: 0, success: 0, error: 0, skipped: 0 },
    complement: { selected: 0, success: 0, error: 0, skipped: 0 },
    item: { selected: 0, success: 0, error: 0, skipped: 0 },
});

const cloneSummary = (summary) =>
    ENTITY_KEYS.reduce((acc, key) => {
        acc[key] = { ...summary[key] };
        return acc;
    }, {});

const extractProductIdsFromSubCategories = (subCategoryIds = [], allSubCategories = {}, acc = new Set()) => {
    subCategoryIds.forEach((subId) => {
        const subCategory = allSubCategories[subId];
        if (!subCategory) return;

        (subCategory.products || []).forEach((productId) => acc.add(productId));
        if (subCategory.subCategories?.length) {
            extractProductIdsFromSubCategories(subCategory.subCategories, allSubCategories, acc);
        }
    });

    return acc;
};

const parseIfoodError = (error, fallbackMessage) => {
    const errorData = error?.response?.data?.data || {};
    const message = errorData.errorMessage || error?.response?.data?.message || fallbackMessage;
    const entityId = errorData.entityId;
    return { message, entityId };
};

const IfoodRegisterWizard = ({ open, handleOpen, sharedLocalData, storeInfo, ifoodData }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [isSaving, setIsSaving] = useState(false);
    const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
    const [selectedProductIds, setSelectedProductIds] = useState([]);
    const [selectedComplementCategoryIds, setSelectedComplementCategoryIds] = useState([]);
    const [selectedComplementIds, setSelectedComplementIds] = useState([]);
    const [result, setResult] = useState(null);
    const [publishSummary, setPublishSummary] = useState(createEmptySummary());
    const [publishProgress, setPublishProgress] = useState({
        total: 0,
        completed: 0,
        current: null,
    });
    const wasOpenRef = useRef(false);

    const {
        categories = [],
        productsById = {},
        subCategoriesById = {},
        complementCategoriesById = {},
        complementsById = {},
    } = sharedLocalData || {};

    const {
        ifoodCategories = {},
        ifoodProducts = {},
        ifoodComplementsCategories = {},
        ifoodComplements = {},
        ifoodItems = {},
    } = ifoodData || {};

    const categoryProductsMap = useMemo(() => {
        const map = {};
        categories.forEach((category) => {
            const productIds = new Set(category.products || []);
            extractProductIdsFromSubCategories(category.subCategories || [], subCategoriesById, productIds);
            map[category.id] = Array.from(productIds).filter((id) => Boolean(productsById[id]));
        });
        return map;
    }, [categories, subCategoriesById, productsById]);

    const availableCategories = useMemo(
        () =>
            categories
                .map((category) => ({
                    id: category.id,
                    name: category.name || "",
                    productIds: categoryProductsMap[category.id] || [],
                    isRegistered: Boolean(ifoodCategories?.[category.id]?.ifoodId),
                }))
                .filter((category) => category.productIds.length > 0),
        [categories, categoryProductsMap, ifoodCategories]
    );

    const productToCategoryMap = useMemo(() => {
        const map = {};
        selectedCategoryIds.forEach((categoryId) => {
            (categoryProductsMap[categoryId] || []).forEach((productId) => {
                if (!map[productId]) map[productId] = categoryId;
            });
        });
        return map;
    }, [selectedCategoryIds, categoryProductsMap]);

    const availableProducts = useMemo(() => {
        const ids = new Set();
        selectedCategoryIds.forEach((categoryId) => {
            (categoryProductsMap[categoryId] || []).forEach((productId) => ids.add(productId));
        });
        return Array.from(ids)
            .map((id) => productsById[id])
            .filter(Boolean)
            .map((product) => ({
                id: product.id,
                name: product.name || "",
                delivery_code: product.delivery_code || "",
                complements: product.complements || [],
                categoryId: productToCategoryMap[product.id] || null,
                isRegistered: Boolean(ifoodItems?.[product.id]?.ifoodId),
            }));
    }, [selectedCategoryIds, categoryProductsMap, productsById, ifoodItems, productToCategoryMap]);

    const availableProductsByCategory = useMemo(
        () =>
            selectedCategoryIds
                .map((categoryId) => ({
                    id: categoryId,
                    name: categories.find((category) => category.id === categoryId)?.name || "",
                    products: availableProducts.filter((product) => product.categoryId === categoryId),
                }))
                .filter((group) => group.products.length > 0),
        [selectedCategoryIds, categories, availableProducts]
    );

    const availableComplementCategories = useMemo(() => {
        const ids = new Set();
        selectedProductIds.forEach((productId) => {
            const product = productsById[productId];
            (product?.complements || []).forEach((id) => ids.add(id));
        });
        return Array.from(ids)
            .map((id) => complementCategoriesById[id])
            .filter(Boolean)
            .map((category) => ({
                id: category.id,
                name: category.name || "",
                complements: category.complements || [],
                isRegistered: Boolean(ifoodComplementsCategories?.[category.id]?.ifoodId),
            }));
    }, [selectedProductIds, productsById, complementCategoriesById, ifoodComplementsCategories]);

    const availableComplements = useMemo(() => {
        const ids = new Set();
        selectedComplementCategoryIds.forEach((categoryId) => {
            const category = complementCategoriesById[categoryId];
            (category?.complements || []).forEach((id) => ids.add(id));
        });
        return Array.from(ids)
            .map((id) => complementsById[id])
            .filter(Boolean)
            .map((complement) => ({
                id: complement.id,
                name: complement.name || "",
                delivery_code: complement.delivery_code || "",
                isRegistered: Boolean(ifoodComplements?.[complement.id]?.ifoodId),
            }));
    }, [selectedComplementCategoryIds, complementCategoriesById, complementsById, ifoodComplements]);

    const availableComplementsByCategory = useMemo(
        () =>
            selectedComplementCategoryIds
                .map((categoryId) => {
                    const category = complementCategoriesById[categoryId];
                    const complements = (category?.complements || [])
                        .map((id) => complementsById[id])
                        .filter(Boolean)
                        .map((complement) => ({
                            id: complement.id,
                            name: complement.name || "",
                            delivery_code: complement.delivery_code || "",
                            isRegistered: Boolean(ifoodComplements?.[complement.id]?.ifoodId),
                        }));

                    return {
                        id: categoryId,
                        name: category?.name || "",
                        complements,
                    };
                })
                .filter((group) => group.complements.length > 0),
        [selectedComplementCategoryIds, complementCategoriesById, complementsById, ifoodComplements]
    );

    const lockedCategoryIds = useMemo(
        () => new Set(availableCategories.filter((item) => item.isRegistered).map((item) => item.id)),
        [availableCategories]
    );
    const lockedProductIds = useMemo(
        () => new Set(availableProducts.filter((item) => item.isRegistered).map((item) => item.id)),
        [availableProducts]
    );
    const lockedComplementCategoryIds = useMemo(
        () => new Set(availableComplementCategories.filter((item) => item.isRegistered).map((item) => item.id)),
        [availableComplementCategories]
    );
    const lockedComplementIds = useMemo(
        () => new Set(availableComplements.filter((item) => item.isRegistered).map((item) => item.id)),
        [availableComplements]
    );

    const resetPublishFeedback = () => {
        setResult(null);
        setPublishSummary(createEmptySummary());
        setPublishProgress({
            total: 0,
            completed: 0,
            current: null,
        });
    };

    useEffect(() => {
        // Only initialize when modal transitions from closed -> open.
        if (!open || wasOpenRef.current) return;

        const defaultCategoryIds = availableCategories
            .filter((category) => Boolean(ifoodCategories?.[category.id]?.ifoodId))
            .map((category) => category.id);

        const defaultProductIds = Array.from(
            new Set(
                defaultCategoryIds.flatMap((categoryId) => categoryProductsMap[categoryId] || [])
            )
        ).filter((productId) => Boolean(ifoodItems?.[productId]?.ifoodId));

        const defaultComplementCategoryIds = Array.from(
            new Set(
                defaultProductIds.flatMap((productId) => productsById[productId]?.complements || [])
            )
        ).filter((categoryId) => Boolean(ifoodComplementsCategories?.[categoryId]?.ifoodId));

        const defaultComplementIds = Array.from(
            new Set(
                defaultComplementCategoryIds.flatMap((categoryId) => complementCategoriesById[categoryId]?.complements || [])
            )
        ).filter((complementId) => Boolean(ifoodComplements?.[complementId]?.ifoodId));

        setCurrentStep(0);
        resetPublishFeedback();
        setSelectedCategoryIds(defaultCategoryIds);
        setSelectedProductIds(defaultProductIds);
        setSelectedComplementCategoryIds(defaultComplementCategoryIds);
        setSelectedComplementIds(defaultComplementIds);
    }, [
        open,
        availableCategories,
        categoryProductsMap,
        productsById,
        complementCategoriesById,
        ifoodCategories,
        ifoodItems,
        ifoodComplementsCategories,
        ifoodComplements,
    ]);

    useEffect(() => {
        wasOpenRef.current = open;
    }, [open]);

    useEffect(() => {
        const availableIds = new Set(availableProducts.map((item) => item.id));
        setSelectedProductIds((prev) => prev.filter((id) => availableIds.has(id)));
    }, [availableProducts]);

    useEffect(() => {
        const availableIds = new Set(availableComplementCategories.map((item) => item.id));
        setSelectedComplementCategoryIds((prev) => prev.filter((id) => availableIds.has(id)));
    }, [availableComplementCategories]);

    useEffect(() => {
        const availableIds = new Set(availableComplements.map((item) => item.id));
        setSelectedComplementIds((prev) => prev.filter((id) => availableIds.has(id)));
    }, [availableComplements]);

    const toggleId = (id, selected, setter, lockedIds = new Set()) => {
        if (selected.includes(id)) {
            if (lockedIds.has(id)) {
                PreAlert.info("Entidade já cadastrada no iFood não pode ser desmarcada.");
                return;
            }
            setter(selected.filter((item) => item !== id));
            return;
        }
        setter([...selected, id]);
    };

    const canGoNext = () => {
        if (currentStep === 0) return selectedCategoryIds.length > 0;
        if (currentStep === 1) return selectedProductIds.length > 0;
        return true;
    };

    const previewSummary = useMemo(
        () => ({
            category: {
                selected: selectedCategoryIds.length,
                success: publishSummary.category.success,
                error: publishSummary.category.error,
                skipped: publishSummary.category.skipped,
            },
            product: {
                selected: selectedProductIds.length,
                success: publishSummary.product.success,
                error: publishSummary.product.error,
                skipped: publishSummary.product.skipped,
            },
            complementCategory: {
                selected: selectedComplementCategoryIds.length,
                success: publishSummary.complementCategory.success,
                error: publishSummary.complementCategory.error,
                skipped: publishSummary.complementCategory.skipped,
            },
            complement: {
                selected: selectedComplementIds.length,
                success: publishSummary.complement.success,
                error: publishSummary.complement.error,
                skipped: publishSummary.complement.skipped,
            },
            item: {
                selected: selectedProductIds.length,
                success: publishSummary.item.success,
                error: publishSummary.item.error,
                skipped: publishSummary.item.skipped,
            },
        }),
        [publishSummary, selectedCategoryIds, selectedProductIds, selectedComplementCategoryIds, selectedComplementIds]
    );

    const previewTotal =
        selectedCategoryIds.length +
        selectedProductIds.length +
        selectedComplementCategoryIds.length +
        selectedComplementIds.length +
        selectedProductIds.length;

    const doPost = async ({ route, payload, entity, entityId, entityName }) => {
        try {
            await api.post(route, payload);
            return { entity, entityId, entityName, route, status: "success", message: "" };
        } catch (error) {
            const parsed = parseIfoodError(error, "Erro ao publicar");
            const messageWithEntity = parsed.entityId
                ? `${parsed.message} (${parsed.entityId})`
                : parsed.message;
            return {
                entity,
                entityId,
                entityName,
                route,
                status: "error",
                message: messageWithEntity,
            };
        }
    };

    const publishAll = async () => {
        if (!storeInfo) {
            PreAlert.error("storeInfo não encontrado.");
            return;
        }

        setIsSaving(true);
        const logs = [];
        const summary = createEmptySummary();
        summary.category.selected = selectedCategoryIds.length;
        summary.product.selected = selectedProductIds.length;
        summary.complementCategory.selected = selectedComplementCategoryIds.length;
        summary.complement.selected = selectedComplementIds.length;
        summary.item.selected = selectedProductIds.length;
        setPublishSummary(cloneSummary(summary));
        setPublishProgress({
            total:
                summary.category.selected +
                summary.product.selected +
                summary.complementCategory.selected +
                summary.complement.selected +
                summary.item.selected,
            completed: 0,
            current: null,
        });

        const addLog = (entry) => {
            logs.push(entry);
            if (!summary[entry.entity]) return;
            summary[entry.entity][entry.status] += 1;
            setPublishSummary(cloneSummary(summary));
            setPublishProgress((prev) => ({ ...prev, completed: prev.completed + 1, current: null }));
        };

        for (const categoryId of selectedCategoryIds) {
            const category = categories.find((item) => item.id === categoryId);
            setPublishProgress((prev) => ({
                ...prev,
                current: { entity: "category", entityName: category?.name || "", entityId: categoryId },
            }));

            if (ifoodCategories?.[categoryId]?.ifoodId) {
                addLog({
                    entity: "category",
                    entityId: categoryId,
                    entityName: category?.name || "",
                    route: "routesIfood/registerCategory",
                    status: "skipped",
                    message: "Categoria já cadastrada no iFood",
                });
                continue;
            }

            const entry = await doPost({
                route: "routesIfood/registerCategory",
                payload: {
                    storeInfo,
                    categorieInfo: { id: categoryId, name: category?.name || "" },
                },
                entity: "category",
                entityId: categoryId,
                entityName: category?.name || "",
            });
            addLog(entry);
        }

        for (const productId of selectedProductIds) {
            const product = productsById[productId];
            setPublishProgress((prev) => ({
                ...prev,
                current: { entity: "product", entityName: product?.name || "", entityId: productId },
            }));

            if (ifoodProducts?.[productId]?.ifoodId) {
                addLog({
                    entity: "product",
                    entityId: productId,
                    entityName: product?.name || "",
                    route: "routesIfood/registerProduct",
                    status: "skipped",
                    message: "Produto já cadastrado no iFood",
                });
                continue;
            }

            const entry = await doPost({
                route: "routesIfood/registerProduct",
                payload: {
                    storeInfo,
                    productInfo: { id: productId, name: product?.name || "" },
                },
                entity: "product",
                entityId: productId,
                entityName: product?.name || "",
            });
            addLog(entry);
        }

        for (const complementCategoryId of selectedComplementCategoryIds) {
            const category = complementCategoriesById[complementCategoryId];
            setPublishProgress((prev) => ({
                ...prev,
                current: {
                    entity: "complementCategory",
                    entityName: category?.name || "",
                    entityId: complementCategoryId,
                },
            }));

            if (ifoodComplementsCategories?.[complementCategoryId]?.ifoodId) {
                addLog({
                    entity: "complementCategory",
                    entityId: complementCategoryId,
                    entityName: category?.name || "",
                    route: "routesIfood/registerComplementCategory",
                    status: "skipped",
                    message: "Categoria de complemento já cadastrada no iFood",
                });
                continue;
            }

            const entry = await doPost({
                route: "routesIfood/registerComplementCategory",
                payload: {
                    storeInfo,
                    categorieInfo: { id: complementCategoryId, name: category?.name || "" },
                },
                entity: "complementCategory",
                entityId: complementCategoryId,
                entityName: category?.name || "",
            });
            addLog(entry);
        }

        for (const complementId of selectedComplementIds) {
            const complement = complementsById[complementId];
            setPublishProgress((prev) => ({
                ...prev,
                current: { entity: "complement", entityName: complement?.name || "", entityId: complementId },
            }));

            if (ifoodComplements?.[complementId]?.ifoodId) {
                addLog({
                    entity: "complement",
                    entityId: complementId,
                    entityName: complement?.name || "",
                    route: "routesIfood/registerComplement",
                    status: "skipped",
                    message: "Complemento já cadastrado no iFood",
                });
                continue;
            }

            const entry = await doPost({
                route: "routesIfood/registerComplement",
                payload: {
                    storeInfo,
                    complementInfo: { id: complementId, name: complement?.name || "" },
                },
                entity: "complement",
                entityId: complementId,
                entityName: complement?.name || "",
            });
            addLog(entry);
        }

        for (const productId of selectedProductIds) {
            const product = productsById[productId];
            const categoryId = productToCategoryMap[productId];
            const existingItem = ifoodItems?.[productId]?.ifoodId;
            setPublishProgress((prev) => ({
                ...prev,
                current: { entity: "item", entityName: product?.name || "", entityId: productId },
            }));

            if (existingItem) {
                addLog({
                    entity: "item",
                    entityId: productId,
                    entityName: product?.name || "",
                    route: "routesIfood/registerItem",
                    status: "skipped",
                    message: "Item já cadastrado no iFood",
                });
                continue;
            }

            if (!categoryId) {
                addLog({
                    entity: "item",
                    entityId: productId,
                    entityName: product?.name || "",
                    route: "routesIfood/registerItem",
                    status: "error",
                    message: "Produto sem categoria selecionada para criação do item",
                });
                continue;
            }

            const entry = await doPost({
                route: "routesIfood/registerItem",
                payload: { storeInfo, productId, categoryId },
                entity: "item",
                entityId: productId,
                entityName: product?.name || "",
            });
            addLog(entry);
        }

        setResult({ logs, summary });
        setPublishSummary(cloneSummary(summary));
        setPublishProgress((prev) => ({ ...prev, current: null, completed: prev.total }));
        setIsSaving(false);
    };

    const copyReport = async () => {
        if (!result) return;
        try {
            await navigator.clipboard.writeText(JSON.stringify(result, null, 2));
            PreAlert.success("Relatório copiado.");
        } catch (error) {
            PreAlert.error("Não foi possível copiar o relatório.");
        }
    };

    const closeModal = () => {
        setCurrentStep(0);
        resetPublishFeedback();
        handleOpen(false);
    };

    return (
        <Modal
            id="modal-ifood-register-wizard"
            isOpen={open}
            setIsOpen={handleOpen}
            isStaticBackdrop
            isCentered
            size="xl"
        >
            <ModalHeader setIsOpen={closeModal}>
                <ModalTitle id="modal-title-ifood-register">
                    Cadastro de Menu iFood
                </ModalTitle>
            </ModalHeader>
            <ModalBody>
                <div className="d-flex gap-2 mb-3 flex-wrap">
                    {STEP_TITLES.map((title, index) => (
                        <Badge key={title} color={index === currentStep ? "dark" : "light"} isLight={index !== currentStep}>
                            {index + 1}. {title}
                        </Badge>
                    ))}
                </div>

                {currentStep === 0 && (
                    <Card>
                        <CardHeader><CardTitle>Selecione as categorias</CardTitle></CardHeader>
                        <CardBody>
                            <div className="d-flex gap-2 mb-3">
                                <Button size="sm" isLight color="dark" onClick={() => setSelectedCategoryIds(availableCategories.map((item) => item.id))}>
                                    Selecionar todas
                                </Button>
                                <Button
                                    size="sm"
                                    isLight
                                    color="dark"
                                    onClick={() => setSelectedCategoryIds(Array.from(lockedCategoryIds))}
                                >
                                    Limpar
                                </Button>
                            </div>
                            <div className="row">
                                {availableCategories.map((item) => {
                                    const checked = selectedCategoryIds.includes(item.id);
                                    return (
                                        <div key={item.id} className="col-md-6 mb-2">
                                            <Card
                                                className={checked ? "border-dark bg-light" : ""}
                                                style={{ cursor: "pointer" }}
                                                onClick={() => toggleId(item.id, selectedCategoryIds, setSelectedCategoryIds, lockedCategoryIds)}
                                            >
                                                <CardBody className="p-3">
                                                    <Checks type="checkbox" checked={checked} readOnly className="me-2" />
                                                    <strong>{item.name}</strong>
                                                    <small className="text-muted ms-2">({item.productIds.length} produtos)</small>
                                                    {item.isRegistered && <small className="text-success ms-2">(já cadastrado)</small>}
                                                </CardBody>
                                            </Card>
                                        </div>
                                    );
                                })}
                            </div>
                        </CardBody>
                    </Card>
                )}

                {currentStep === 1 && (
                    <Card>
                        <CardHeader><CardTitle>Selecione os produtos</CardTitle></CardHeader>
                        <CardBody>
                            <div className="d-flex gap-2 mb-3">
                                <Button size="sm" isLight color="dark" onClick={() => setSelectedProductIds(availableProducts.map((item) => item.id))}>
                                    Selecionar todos
                                </Button>
                                <Button
                                    size="sm"
                                    isLight
                                    color="dark"
                                    onClick={() => setSelectedProductIds(Array.from(lockedProductIds))}
                                >
                                    Limpar
                                </Button>
                            </div>
                            {!availableProductsByCategory.length ? (
                                <Alert color="info" isLight>Nenhum produto disponível para as categorias selecionadas.</Alert>
                            ) : (
                                <div className="d-flex flex-column gap-3">
                                    {availableProductsByCategory.map((group) => (
                                        <div key={group.id}>
                                            <h6 className="mb-2">{group.name || "Sem categoria"}</h6>
                                            <div className="row">
                                                {group.products.map((item) => {
                                                    const checked = selectedProductIds.includes(item.id);
                                                    return (
                                                        <div key={item.id} className="col-md-6 mb-2">
                                                            <Card
                                                                className={checked ? "border-dark bg-light" : ""}
                                                                style={{ cursor: "pointer" }}
                                                                onClick={() =>
                                                                    toggleId(item.id, selectedProductIds, setSelectedProductIds, lockedProductIds)
                                                                }
                                                            >
                                                                <CardBody className="p-3">
                                                                    <Checks type="checkbox" checked={checked} readOnly className="me-2" />
                                                                    <strong>{item.name}</strong>
                                                                    <small className="text-muted ms-2">{item.delivery_code || "-"}</small>
                                                                    {item.isRegistered && (
                                                                        <small className="text-success ms-2">(já cadastrado)</small>
                                                                    )}
                                                                </CardBody>
                                                            </Card>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardBody>
                    </Card>
                )}

                {currentStep === 2 && (
                    <Card>
                        <CardHeader><CardTitle>Selecione categorias de complemento</CardTitle></CardHeader>
                        <CardBody>
                            {!availableComplementCategories.length ? (
                                <Alert color="info" isLight>Nenhuma categoria de complemento encontrada para os produtos selecionados.</Alert>
                            ) : (
                                <>
                                    <div className="d-flex gap-2 mb-3">
                                        <Button size="sm" isLight color="dark" onClick={() => setSelectedComplementCategoryIds(availableComplementCategories.map((item) => item.id))}>
                                            Selecionar todas
                                        </Button>
                                        <Button
                                            size="sm"
                                            isLight
                                            color="dark"
                                            onClick={() => setSelectedComplementCategoryIds(Array.from(lockedComplementCategoryIds))}
                                        >
                                            Limpar
                                        </Button>
                                    </div>
                                    <div className="row">
                                        {availableComplementCategories.map((item) => {
                                            const checked = selectedComplementCategoryIds.includes(item.id);
                                            return (
                                                <div key={item.id} className="col-md-6 mb-2">
                                                    <Card
                                                        className={checked ? "border-dark bg-light" : ""}
                                                        style={{ cursor: "pointer" }}
                                                        onClick={() =>
                                                            toggleId(
                                                                item.id,
                                                                selectedComplementCategoryIds,
                                                                setSelectedComplementCategoryIds,
                                                                lockedComplementCategoryIds
                                                            )
                                                        }
                                                    >
                                                        <CardBody className="p-3">
                                                            <Checks type="checkbox" checked={checked} readOnly className="me-2" />
                                                            <strong>{item.name}</strong>
                                                            {item.isRegistered && <small className="text-success ms-2">(já cadastrado)</small>}
                                                        </CardBody>
                                                    </Card>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </>
                            )}
                        </CardBody>
                    </Card>
                )}

                {currentStep === 3 && (
                    <Card>
                        <CardHeader><CardTitle>Selecione complementos</CardTitle></CardHeader>
                        <CardBody>
                            {!availableComplementsByCategory.length ? (
                                <Alert color="info" isLight>Nenhum complemento disponível para as categorias selecionadas.</Alert>
                            ) : (
                                <>
                                    <div className="d-flex gap-2 mb-3">
                                        <Button size="sm" isLight color="dark" onClick={() => setSelectedComplementIds(availableComplements.map((item) => item.id))}>
                                            Selecionar todos
                                        </Button>
                                        <Button
                                            size="sm"
                                            isLight
                                            color="dark"
                                            onClick={() => setSelectedComplementIds(Array.from(lockedComplementIds))}
                                        >
                                            Limpar
                                        </Button>
                                    </div>
                                    <div className="d-flex flex-column gap-3">
                                        {availableComplementsByCategory.map((group) => (
                                            <div key={group.id}>
                                                <h6 className="mb-2">{group.name || "Sem categoria"}</h6>
                                                <div className="row">
                                                    {group.complements.map((item) => {
                                                        const checked = selectedComplementIds.includes(item.id);
                                                        return (
                                                            <div key={`${group.id}-${item.id}`} className="col-md-6 mb-2">
                                                                <Card
                                                                    className={checked ? "border-dark bg-light" : ""}
                                                                    style={{ cursor: "pointer" }}
                                                                    onClick={() =>
                                                                        toggleId(
                                                                            item.id,
                                                                            selectedComplementIds,
                                                                            setSelectedComplementIds,
                                                                            lockedComplementIds
                                                                        )
                                                                    }
                                                                >
                                                                    <CardBody className="p-3">
                                                                        <Checks type="checkbox" checked={checked} readOnly className="me-2" />
                                                                        <strong>{item.name}</strong>
                                                                        <small className="text-muted ms-2">{item.delivery_code || "-"}</small>
                                                                        {item.isRegistered && (
                                                                            <small className="text-success ms-2">(já cadastrado)</small>
                                                                        )}
                                                                    </CardBody>
                                                                </Card>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </CardBody>
                    </Card>
                )}

                {currentStep === 4 && (
                    <Card>
                        <CardHeader><CardTitle>Preview e Publicação</CardTitle></CardHeader>
                        <CardBody>
                            {
                                <Card >
                                    <CardBody>
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <strong>Progresso de publicação</strong>
                                            <small className="text-muted">
                                                {publishProgress.completed}/{publishProgress.total || previewTotal}
                                            </small>
                                        </div>
                                        <div className="progress mb-2" style={{ height: 8 }}>
                                            <div
                                                className="progress-bar bg-dark"
                                                role="progressbar"
                                                style={{
                                                    width: `${
                                                        publishProgress.total
                                                            ? Math.round(
                                                                (publishProgress.completed / publishProgress.total) * 100
                                                            )
                                                            : 0
                                                    }%`,
                                                }}
                                            />
                                        </div>
                                        <small className="text-muted d-block">
                                            {publishProgress.current
                                                ? `Em andamento: ${
                                                    ENTITY_LABELS[publishProgress.current.entity]
                                                } - ${publishProgress.current.entityName || publishProgress.current.entityId}`
                                                : isSaving
                                                    ? "Preparando próxima execução..."
                                                    : "Sem execução em andamento."}
                                        </small>

                                        <div className="table-responsive mt-3">
                                            <table className="table table-sm table-striped mb-0">
                                                <thead>
                                                    <tr>
                                                        <th>Entidade</th>
                                                        <th>Já cadastrados</th>
                                                        <th>Cadastrados</th>
                                                        <th>Em andamento</th>
                                                        <th>Pendentes</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {ENTITY_KEYS.map((key) => {
                                                        const entitySummary = previewSummary[key];
                                                        const inProgress = publishProgress.current?.entity === key ? 1 : 0;
                                                        const alreadyRegistered = entitySummary.skipped;
                                                        const created = entitySummary.success;
                                                        const handled = created + entitySummary.error + alreadyRegistered;
                                                        const pending = Math.max(
                                                            entitySummary.selected - handled - inProgress,
                                                            0
                                                        );
                                                        return (
                                                            <tr key={`progress-${key}`}>
                                                                <td>{ENTITY_LABELS[key]}</td>
                                                                <td>{alreadyRegistered}</td>
                                                                <td>{created}</td>
                                                                <td>{inProgress}</td>
                                                                <td>{pending}</td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </CardBody>
                                </Card>
                            }

                            {result && (
                                <>
                                    <h6 className="mt-3">Falhas</h6>
                                    {result.logs.filter((item) => item.status === "error").length === 0 ? (
                                        <Alert color="success" isLight>Nenhuma falha.</Alert>
                                    ) : (
                                        <div className="table-responsive">
                                            <table className="table table-sm table-striped">
                                                <thead>
                                                    <tr>
                                                        <th>Entidade</th>
                                                        <th>ID</th>
                                                        <th>Nome</th>
                                                        <th>Rota</th>
                                                        <th>Status</th>
                                                        <th>Mensagem</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {result.logs
                                                        .filter((item) => item.status === "error")
                                                        .map((item, idx) => (
                                                            <tr key={`${item.entity}-${item.entityId}-${idx}`}>
                                                                <td>{item.entity}</td>
                                                                <td>{item.entityId}</td>
                                                                <td>{item.entityName}</td>
                                                                <td>{item.route}</td>
                                                                <td>{item.status}</td>
                                                                <td>{item.message}</td>
                                                            </tr>
                                                        ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </>
                            )}
                        </CardBody>
                    </Card>
                )}
            </ModalBody>
            <ModalFooter>

                {currentStep > 0 && (
                    <Button color="dark" isLight onClick={() => setCurrentStep((prev) => prev - 1)} disabled={isSaving}>
                        Voltar
                    </Button>
                )}

                {currentStep < STEP_TITLES.length - 1 ? (
                    <Button color="dark" onClick={() => setCurrentStep((prev) => prev + 1)} disabled={!canGoNext() || isSaving}>
                        Próximo
                    </Button>
                ) : (
                    <>
                        <Button color="dark" icon={isSaving ? "HourglassEmpty" : "save"} onClick={publishAll} disabled={isSaving || !selectedProductIds.length}>
                            Publicar selecionados
                        </Button>
                        <Button color="info" isLight onClick={copyReport} disabled={!result}>
                            Copiar relatório
                        </Button>
                    </>
                )}
            </ModalFooter>
        </Modal>
    );
};

export default IfoodRegisterWizard;
